import { Component, DestroyRef, inject } from '@angular/core';
import { InteractionStatus } from '@azure/msal-browser';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AccountService } from '../../core/services/account-service';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '../../core/services/snackbar-service';
import { environment } from '../../../environments/environment';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, filter, take } from 'rxjs';

@Component({
  selector: 'app-login-page',
  imports: [ReactiveFormsModule, MatFormField, MatInput, MatLabel, MatButton],
  templateUrl: './login-page.html',
  styleUrl: './login-page.scss',
})
export class LoginPage {
  private fb = inject(FormBuilder);
  accountService = inject(AccountService);
  private router = inject(Router);
  private snackbar = inject(SnackbarService);
  private route = inject(ActivatedRoute);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private destroyRef = inject(DestroyRef);

  production = environment.production;
  msalEnabled = !!environment.msalConfig.auth.clientId;
  msalInteractionInProgress = true;
  microsoftLoginStarting = false;
  height = 0;

  loginForm = this.fb.group({
    username: [''],
    password: [''],
  });

  ngOnInit(): void {
    this.msalBroadcastService.inProgress$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (status) =>
          (this.msalInteractionInProgress = status !== InteractionStatus.None),
      );

    this.accountService.user$.subscribe((user) => {
      if (user) {
        this.route.queryParams.subscribe((params) => {
          // const returnUrl = params['returnUrl'] || '/';
          // this.router.navigate([returnUrl]);
          this.router.navigate(['/']);
        });
      }
    });
  }

  onSubmit(): void {
    this.accountService
      .login(this.loginForm.value.username, this.loginForm.value.password)
      .subscribe({
        next: () => {
          // get return url from query
          this.route.queryParams.subscribe((params) => {
            // const returnUrl = params['returnUrl'] || '/';
            // this.router.navigate([returnUrl]);
            this.router.navigate(['/']);
          });
        },
        error: (error) => {
          this.snackbar.error(error.error || 'Login failed');
        },
      });
  }

  async signInWithMicrosoft(): Promise<void> {
    if (
      !this.msalEnabled ||
      this.msalInteractionInProgress ||
      this.microsoftLoginStarting
    ) {
      return;
    }

    this.microsoftLoginStarting = true;
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];
    const request = {
      scopes: environment.apiConfig.scopes,
      loginHint: account?.username,
    };

    try {
      await firstValueFrom(
        this.msalBroadcastService.inProgress$.pipe(
          filter((status) => status === InteractionStatus.None),
          take(1),
        ),
      );

      this.msalService.loginRedirect(request).subscribe({
        error: (error) => {
          this.microsoftLoginStarting = false;
          console.error('Microsoft sign-in failed:', error);
          this.snackbar.error('Microsoft sign-in could not be started.');
        },
      });
    } catch (error) {
      this.microsoftLoginStarting = false;
      console.error('Microsoft sign-in failed:', error);
      this.snackbar.error('Microsoft sign-in could not be started.');
    }
  }
}
